import React from "react";
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, ReferenceLine, LabelList } from "recharts";
import { useLang } from "../lib/i18n";

const ZONE_COLORS = ["#D97706", "#DC2626", "#7F1D1D", "#0284C7"];
const FUNNEL_COLORS = ["#F05A28", "#D97706", "#059669", "#0284C7", "#7C3AED", "#0F172A"];
const PIE_COLORS = ["#F05A28", "#0284C7", "#059669", "#D97706", "#94A3B8"];

const Card = ({ title, children, testid, className = "" }) => (
  <div className={`bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-4 ${className}`} data-testid={testid}>
    <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">{title}</h3>
    {children}
  </div>
);

/** Charts + plain-language interpretations stored on a derived report. */
export function ReportDashboard({ dash }) {
  const { t } = useLang();
  if (!dash) return null;
  const trend = (dash.trend || []).length > (dash.series || []).length ? dash.trend : dash.series || [];
  const compare = dash.compare || [];
  const zones = dash.zones || [];
  const zoneKeys = dash.zone_keys || [];
  const funnel = dash.funnel || [];
  const rates = dash.rates || [];
  const sources = (dash.sources || []).filter((s) => s.value);
  const interp = dash.interpretations || [];
  const label = (code) => (dash.series_fields || []).find((f) => f.code === code)?.label || compare.find((c) => c.code === code)?.label || code;
  if (trend.length < 2 && !compare.length && zones.length < 2 && !funnel.length && !rates.length) return null;

  return (
    <section className="mb-4" data-testid="gv-dashboard">
      <h2 className="text-sm font-semibold text-slate-900 mb-3">{t("Tableau de bord de la période")}</h2>

      {interp.length > 0 && (
        <div className="bg-[#FFF8F5] border border-[#FDBA88] rounded-lg p-4 mb-4" data-testid="gv-interpretations">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-[#F05A28] mb-2">{t("Ce que disent les chiffres")}</h3>
          <ul className="space-y-1.5">
            {interp.map((it) => (
              <li key={it.code} className="text-sm text-slate-700 leading-relaxed flex gap-2" data-testid={`gv-interp-${it.code}`}>
                <span className="text-[#F05A28] shrink-0">•</span><span>{it.text}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {trend.length > 1 && (
          <Card title={t(trend === dash.trend ? "Tendance (jusqu'à 28 derniers jours)" : "Évolution sur la période")} testid="gv-chart-series">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={trend.map((s) => ({ ...s, d: s.date.slice(5) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" />
                <XAxis dataKey="d" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
                {(dash.series_fields || []).map((f) => (
                  <Line key={f.code} type="monotone" dataKey={f.code} name={f.label} stroke={f.color} strokeWidth={2} dot={{ r: 2.5 }} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Card>
        )}
        {funnel.length > 0 && (
          <Card title={t("Chaîne des alertes : de la notification à la prise en charge")} testid="gv-chart-funnel">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={funnel} margin={{ top: 15 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} allowDecimals={false} /><Tooltip />
                <Bar dataKey="value" name={t("Alertes")} radius={[3, 3, 0, 0]}>
                  <LabelList dataKey="value" position="top" style={{ fontSize: 10, fill: "#1E293B" }} />
                  {funnel.map((_, i) => <Cell key={i} fill={FUNNEL_COLORS[i % FUNNEL_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
        {compare.length > 0 && (
          <Card title={t("Indicateurs clés : actuel vs précédent")} testid="gv-chart-compare">
            <ResponsiveContainer width="100%" height={Math.max(220, compare.length * 34)}>
              <BarChart data={compare} layout="vertical" margin={{ left: 10, right: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                <YAxis type="category" dataKey="label" tick={{ fontSize: 10 }} width={150} />
                <Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="previous" name={t("Période précédente")} fill="#CBD5E1" radius={[0, 3, 3, 0]} />
                <Bar dataKey="current" name={t("Période actuelle")} fill="#F05A28" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
        {rates.length > 0 && (
          <Card title={t("Indicateurs de performance (%) par rapport à la cible de 80 %")} testid="gv-chart-rates">
            <ResponsiveContainer width="100%" height={Math.max(200, rates.length * 34)}>
              <BarChart data={rates} layout="vertical" margin={{ left: 10, right: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" horizontal={false} />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="label" tick={{ fontSize: 10 }} width={170} />
                <Tooltip formatter={(v) => `${v} %`} />
                <ReferenceLine x={80} stroke="#94A3B8" strokeDasharray="4 4" label={{ value: "cible", fontSize: 10, fill: "#64748B", position: "top" }} />
                <Bar dataKey="value" name="%" radius={[0, 3, 3, 0]}>
                  <LabelList dataKey="value" position="right" formatter={(v) => `${v} %`} style={{ fontSize: 10, fill: "#1E293B" }} />
                  {rates.map((r, i) => <Cell key={i} fill={r.lower_is_better || r.value >= 80 ? "#059669" : "#DC2626"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
        {sources.length > 0 && (
          <Card title={t("Origine des alertes")} testid="gv-chart-sources">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={sources} dataKey="value" nameKey="label" innerRadius={50} outerRadius={85} paddingAngle={2} label={({ percent }) => `${Math.round(percent * 100)} %`}>
                  {sources.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        )}
        {zones.length > 1 && zoneKeys.length > 0 && (
          <Card title={t("Répartition par zone de santé")} testid="gv-chart-zones">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={zones} margin={{ bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" />
                <XAxis dataKey="zone" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={55} interval={0} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} /><Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
                {zoneKeys.map((k, i) => <Bar key={k} dataKey={k} name={label(k)} fill={ZONE_COLORS[i % 4]} radius={[2, 2, 0, 0]} />)}
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>
    </section>
  );
}

export default ReportDashboard;
